import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from './ui/card';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { 
  Users,
  Plus,
  Edit,
  Trash2,
  Shield,
  Settings,
  Eye,
  CheckCircle,
  XCircle,
  RefreshCw
} from 'lucide-react';
import GranularPermissionsSelector from './GranularPermissionsSelector';
import useMenuConfig from '../hooks/useMenuConfig';

const API = process.env.REACT_APP_BACKEND_URL;

// Walk the menu JSON tree to collect IDs matching enabled tabs from old granular_permissions
function convertGranularToPermittedIds(granularPerms, menuItems) {
  const enabledTabIds = new Set();
  for (const mod of granularPerms || []) {
    if (!mod.enabled) continue;
    for (const tab of mod.tabs || []) {
      if (tab.enabled) {
        enabledTabIds.add(tab.id);
        for (const pt of tab.page_tabs || []) {
          if (pt.enabled) enabledTabIds.add(pt.id);
        }
      }
    }
  }
  const result = [];
  const walk = (items) => {
    for (const item of items) {
      if (item.id && enabledTabIds.has(item.id)) result.push(item.id);
      if (item.children) walk(item.children);
    }
  };
  walk(menuItems);
  return result;
}

const UserProfilesPage = () => {
  const { menuConfig } = useMenuConfig();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_admin: false,
    menu_permissions: [],
    granular_permissions: [],
    permitted_ids: [],
    can_manage_signatures: false,
    nivel_acesso_dados: 'proprio'
  });

  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/user-profiles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfiles(response.data);
    } catch (error) {
      console.error('Erro ao carregar perfis:', error);
      toast.error('Erro ao carregar perfis de usuário');
    } finally {
      setLoading(false);
    }
  };

  const countPermissions = (profile) => {
    if (profile.permitted_ids?.length > 0) return profile.permitted_ids.length;
    if (!profile.granular_permissions || profile.granular_permissions.length === 0) {
      return profile.menu_permissions?.length || 0;
    }
    let count = 0;
    profile.granular_permissions.forEach((module) => {
      if (module.enabled && module.tabs && module.tabs.length > 0) {
        module.tabs.forEach((tab) => {
          if (tab.enabled) {
            count++;
            if (tab.page_tabs && tab.page_tabs.length > 0) {
              count += tab.page_tabs.length;
            }
          }
        });
      } else if (module.enabled) {
        count++;
      }
    });
    return count;
  };

  const createDefaultProfiles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/user-profiles/create-defaults`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      await loadProfiles();
    } catch (error) {
      console.error('Erro ao criar perfis padrão:', error);
      toast.error('Erro ao criar perfis padrão');
    }
  };

  const handleCreateProfile = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/user-profiles`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Perfil criado com sucesso!');
      setShowCreateModal(false);
      setFormData({
        name: '',
        description: '',
        is_admin: false,
        menu_permissions: [],
        granular_permissions: [],
        permitted_ids: [],
        can_manage_signatures: false,
        nivel_acesso_dados: 'proprio',
      });
      await loadProfiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar perfil');
    }
  };

  const handleEditProfile = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/user-profiles/${editingProfile.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Perfil atualizado com sucesso!');
      setShowEditModal(false);
      setEditingProfile(null);
      setFormData({
        name: '',
        description: '',
        is_admin: false,
        menu_permissions: [],
        granular_permissions: [],
        permitted_ids: [],
        can_manage_signatures: false,
        nivel_acesso_dados: 'proprio',
      });
      await loadProfiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar perfil');
    }
  };

  const handleDeleteProfile = async (profileId) => {
    if (!window.confirm('Tem certeza que deseja excluir este perfil?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/user-profiles/${profileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Perfil excluído com sucesso!');
      await loadProfiles();
    } catch (error) {
      console.error('Erro ao excluir perfil:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir perfil');
    }
  };

  const openEditModal = (profile) => {
    let initialPermittedIds = profile.permitted_ids || [];
    if (initialPermittedIds.length === 0 && profile.granular_permissions?.length > 0 && menuConfig?.items) {
      initialPermittedIds = convertGranularToPermittedIds(
        profile.granular_permissions,
        menuConfig.items
      );
    }
    setEditingProfile(profile);
    setFormData({
      name: profile.name,
      description: profile.description || '',
      is_admin: profile.is_admin,
      menu_permissions: profile.menu_permissions || [],
      granular_permissions: profile.granular_permissions || [],
      permitted_ids: initialPermittedIds,
      can_manage_signatures: profile.can_manage_signatures,
      nivel_acesso_dados: profile.nivel_acesso_dados || 'proprio',
    });
    setShowEditModal(true);
  };

  const handleMenuPermissionChange = (menuOption, checked) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        menu_permissions: [...prev.menu_permissions, menuOption]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        menu_permissions: prev.menu_permissions.filter(item => item !== menuOption)
      }));
    }
  };

  const getProfileTypeLabel = (profile) => {
    if (profile.is_default) {
      return <Badge variant="default">Padrão</Badge>;
    }
    return <Badge variant="secondary">Personalizado</Badge>;
  };

  const getAdminStatusLabel = (isAdmin) => {
    return isAdmin ? (
      <Badge variant="destructive" className="flex items-center gap-1">
        <Shield className="h-3 w-3" />
        Admin
      </Badge>
    ) : (
      <Badge variant="outline" className="flex items-center gap-1">
        <Users className="h-3 w-3" />
        Usuário
      </Badge>
    );
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Perfis de Usuário</h1>
          <p className="text-gray-600 mt-2">
            Gerencie perfis de permissões para facilitar o cadastro de usuários
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button 
            variant="outline" 
            onClick={createDefaultProfiles}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Criar Perfis Padrão
          </Button>
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Novo Perfil
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Perfis</p>
                <p className="text-2xl font-bold">{profiles.length}</p>
              </div>
              <Users className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Perfis Admin</p>
                <p className="text-2xl font-bold">
                  {profiles.filter(p => p.is_admin).length}
                </p>
              </div>
              <Shield className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Perfis Padrão</p>
                <p className="text-2xl font-bold">
                  {profiles.filter(p => p.is_default).length}
                </p>
              </div>
              <Settings className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Perfis Ativos</p>
                <p className="text-2xl font-bold">
                  {profiles.filter(p => p.is_active).length}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Profiles Table */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Perfis</CardTitle>
          <CardDescription>
            Gerencie os perfis de usuário e suas permissões
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4" />
              <p>Carregando perfis...</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Permissões</TableHead>
                  <TableHead>Assinaturas</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {profiles.map(profile => (
                  <TableRow key={profile.id}>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">{profile.name}</span>
                        {getAdminStatusLabel(profile.is_admin)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-600">
                        {profile.description || 'Sem descrição'}
                      </span>
                    </TableCell>
                    <TableCell>
                      {getProfileTypeLabel(profile)}
                    </TableCell>
                    <TableCell>
                      {profile.is_active ? (
                        <Badge variant="default" className="flex items-center gap-1 w-fit">
                          <CheckCircle className="h-3 w-3" />
                          Ativo
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="flex items-center gap-1 w-fit">
                          <XCircle className="h-3 w-3" />
                          Inativo
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {countPermissions(profile)} telas
                      </span>
                    </TableCell>
                    <TableCell>
                      {profile.can_manage_signatures ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-gray-400" />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditModal(profile)}
                          title="Editar perfil"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteProfile(profile.id)}
                          className="text-red-600 hover:text-red-700"
                          title="Excluir perfil"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {profiles.length === 0 && !loading && (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum perfil encontrado</h3>
              <p className="text-gray-500 mb-4">
                Crie perfis para facilitar a gestão de permissões de usuários
              </p>
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Criar Primeiro Perfil
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Profile Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent 
          className="max-w-2xl max-h-[80vh] flex flex-col"
        >
          <DialogHeader>
            <DialogTitle>Criar Novo Perfil</DialogTitle>
            <DialogDescription>
              Defina as permissões para este perfil de usuário
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateProfile} className="flex-1 min-h-0 flex flex-col">
            <div className="space-y-4 flex-1 min-h-0 flex flex-col overflow-y-auto">
              <div>
                <Label htmlFor="name">Nome do Perfil</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Gestor Financeiro"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descreva as responsabilidades deste perfil"
                  rows={3}
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_admin"
                    checked={formData.is_admin}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_admin: checked }))}
                  />
                  <Label htmlFor="is_admin" className="text-sm font-medium">
                    Administrador do Sistema
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="can_manage_signatures"
                    checked={formData.can_manage_signatures}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, can_manage_signatures: checked }))}
                  />
                  <Label htmlFor="can_manage_signatures" className="text-sm font-medium">
                    Gerenciar Assinaturas Eletrônicas
                  </Label>
                </div>

                {/* NOVO: Nível de Acesso aos Dados */}
                <div className="space-y-2 p-3 border rounded-lg bg-blue-50">
                  <Label className="text-sm font-medium text-blue-900">
                    Nível de Acesso aos Dados
                  </Label>
                  <select
                    value={formData.nivel_acesso_dados || 'proprio'}
                    onChange={(e) => setFormData(prev => ({ ...prev, nivel_acesso_dados: e.target.value }))}
                    className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="proprio">Próprio - Vê apenas seus dados</option>
                    <option value="equipe">Equipe - Vê dados de subordinados</option>
                    <option value="global">Global - Vê todos os dados da empresa</option>
                  </select>
                  <p className="text-xs text-blue-700 mt-2">
                    • <strong>Próprio:</strong> Colaborador vê apenas seus próprios registros<br/>
                    • <strong>Equipe:</strong> Gestor vê seus dados + dados dos subordinados diretos<br/>
                    • <strong>Global:</strong> Administração vê todos os dados da empresa
                  </p>
                </div>
              </div>

              <div className="flex-1 min-h-0 flex flex-col">
                <Label className="text-sm font-medium mb-3 block">
                  Permissões por Item de Menu
                </Label>
                <div className="flex-1 min-h-0">
                  <GranularPermissionsSelector
                    items={menuConfig?.items || []}
                    value={formData.permitted_ids}
                    onChange={(newIds) =>
                      setFormData({ ...formData, permitted_ids: newIds })
                    }
                  />
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" type="button" onClick={() => setShowCreateModal(false)}>
                Cancelar
              </Button>
              <Button type="submit">
                <Plus className="h-4 w-4 mr-2" />
                Criar Perfil
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent 
          className="max-w-2xl max-h-[80vh] flex flex-col"
        >
          <DialogHeader>
            <DialogTitle>Editar Perfil</DialogTitle>
            <DialogDescription>
              Modifique as permissões deste perfil de usuário
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditProfile} className="flex-1 min-h-0 flex flex-col">
            <div className="space-y-4 flex-1 min-h-0 flex flex-col overflow-y-auto">
              <div>
                <Label htmlFor="edit_name">Nome do Perfil</Label>
                <Input
                  id="edit_name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Gestor Financeiro"
                  required
                />
              </div>

              <div>
                <Label htmlFor="edit_description">Descrição</Label>
                <Textarea
                  id="edit_description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descreva as responsabilidades deste perfil"
                  rows={3}
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="edit_is_admin"
                    checked={formData.is_admin}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_admin: checked }))}
                  />
                  <Label htmlFor="edit_is_admin" className="text-sm font-medium">
                    Administrador do Sistema
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="edit_can_manage_signatures"
                    checked={formData.can_manage_signatures}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, can_manage_signatures: checked }))}
                  />
                  <Label htmlFor="edit_can_manage_signatures" className="text-sm font-medium">
                    Gerenciar Assinaturas Eletrônicas
                  </Label>
                </div>

                {/* NOVO: Nível de Acesso aos Dados */}
                <div className="space-y-2 p-3 border rounded-lg bg-blue-50">
                  <Label className="text-sm font-medium text-blue-900">
                    Nível de Acesso aos Dados
                  </Label>
                  <select
                    value={formData.nivel_acesso_dados || 'proprio'}
                    onChange={(e) => setFormData(prev => ({ ...prev, nivel_acesso_dados: e.target.value }))}
                    className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="proprio">Próprio - Vê apenas seus dados</option>
                    <option value="equipe">Equipe - Vê dados de subordinados</option>
                    <option value="global">Global - Vê todos os dados da empresa</option>
                  </select>
                  <p className="text-xs text-blue-700 mt-2">
                    • <strong>Próprio:</strong> Colaborador vê apenas seus próprios registros<br/>
                    • <strong>Equipe:</strong> Gestor vê seus dados + dados dos subordinados diretos<br/>
                    • <strong>Global:</strong> Administração vê todos os dados da empresa
                  </p>
                </div>
              </div>

              <div className="flex-1 min-h-0 flex flex-col">
                <Label className="text-sm font-medium mb-3 block">
                  Permissões por Item de Menu
                </Label>
                <div className="flex-1 min-h-0">
                  <GranularPermissionsSelector
                    items={menuConfig?.items || []}
                    value={formData.permitted_ids}
                    onChange={(newIds) =>
                      setFormData({ ...formData, permitted_ids: newIds })
                    }
                  />
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" type="button" onClick={() => setShowEditModal(false)}>
                Cancelar
              </Button>
              <Button type="submit">
                <Edit className="h-4 w-4 mr-2" />
                Salvar Alterações
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserProfilesPage;